// store/financialDefaults.ts
// Seed states, defaults, and the schema/storage constants shared by the slice,
// migrations, and thunks.

import type { Assumptions, FinancialState } from './financialTypes';

// Current persisted schema version. History (why each bump matters for migration):
//   V4  — expense grouping, surplus allocation, widened expense frequency.
//   V5  — optional per-holding analytics (target allocation, yield, employer).
//   V10 — inflation defaults to 3%; blank pre-V10 values backfilled ONCE on load,
//         a blank saved under V10+ stays off.
//   V11 — projection world is NOMINAL: returnRate converts real→nominal once by
//         adding inflation; incomeGrowthRate (a dead lever before V11) resets to
//         the neutral default (matches inflation).
//   V12 — phase-aware spending: expense rows gain optional endYear + loan fields.
//         All additive; undefined is exactly pre-V12 behavior.
//   V13 — the valuation fix: holdings gain manualCurrentValue +
//         manualValueUpdatedAt + avgPriceSource. Pre-fix avgPrice values are
//         classified by provenance (exact quote match = seeded, otherwise
//         unknown); manualCurrentValue is NEVER populated from old data.
export const SCHEMA_VERSION = 13;

// Year-by-year override cap (0..5 inclusive → 6 entries).
export const MAX_OVERRIDE_YEARS = 6;

export const STORAGE_KEY = 'nisk_financial';

export const uid = (): string => Math.random().toString(36).slice(2, 8);

export const DEFAULT_ASSUMPTIONS: Assumptions = {
	returnRate: '10', // NOMINAL (before inflation); long-run US market average
	withdrawalRate: '4',
	incomeGrowthRate: '3', // nominal wage growth; default matches inflation
	inflationRate: '3', // long-run US average; blank = inflation off
	projectUntilAge: '90',
	portfolioMode: 'simple',
	returnOverrides: [],
	incomeMode: 'afterTax', // default: entered income is already take-home
	estimatedTaxRate: '25', // dormant until user switches to pre-tax
	targetFreedomAge: '', // no goal declared until the person sets one
};

// Logged-in first-run starter profile.
export const DEFAULT_STATE: FinancialState = {
	age: '25',
	savings: '10000',
	investedSavings: '0',
	lifestyleAssets: [],
	currentRunwaySaved: '',
	incomeEntries: [
		{ id: uid(), name: 'Salary', amount: '5000', frequency: 'monthly' },
	],
	expenseEntries: [
		{
			id: uid(),
			name: 'Rent',
			amount: '1500',
			frequency: 'monthly',
			group: 'fixed_essentials',
			flexibility: 'low',
			includeInMonthlySpending: true,
		},
		{
			id: uid(),
			name: 'Groceries',
			amount: '400',
			frequency: 'monthly',
			group: 'flexible_lifestyle',
			flexibility: 'medium',
			includeInMonthlySpending: true,
		},
	],
	surplusAllocations: [],
	holdings: [],
	equityGrants: [],
	assumptions: DEFAULT_ASSUMPTIONS,
	_schemaVersion: SCHEMA_VERSION,
	_lastUpdated: Date.now(),
	_syncStatus: 'idle',
};

// The anonymous zero state. Public-funnel visitors start from an honest EMPTY
// slate: no fake salary, no fake rent, no seeded savings.
export const ANON_ZERO_STATE: FinancialState = {
	...DEFAULT_STATE,
	age: '',
	savings: '0',
	investedSavings: '0',
	incomeEntries: [],
	expenseEntries: [],
};
